import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { SuggestionData } from './SuggestionPopover';

interface ActiveSuggestion {
  anchorEl: HTMLElement;
  sid: string;
  data: SuggestionData;
}

interface UseDocSuggestionPopoverArgs {
  /** Container whose descendants get listened to for `.ed-suggestion` clicks. */
  containerRef: RefObject<HTMLElement | null>;
  /** Lookup table: data-sid -> suggestion data. */
  suggestionsBySid: Record<string, SuggestionData>;
}

interface UseDocSuggestionPopoverReturn {
  active: ActiveSuggestion | null;
  /** Pass to <SuggestionPopover open={...}>. True when a suggestion is open. */
  open: boolean;
  /** Close the popover (call after apply or dismiss). */
  close: () => void;
  /** Mark a suggestion as dismissed — adds `ed-dismissed` to its anchor span
   * so the underline visually fades. */
  dismiss: (sid?: string) => void;
}

/**
 * Listens for clicks on `.ed-suggestion[data-sid]` spans inside `containerRef`
 * and exposes the active suggestion's anchor element + lookup data so a
 * single SuggestionPopover instance can render at the correct position.
 *
 * Mirrors the design's editor-app.jsx click handler — but scoped to a ref
 * instead of a global listener so multiple editor instances on one page do
 * not interfere with each other.
 */
export function useDocSuggestionPopover({
  containerRef,
  suggestionsBySid,
}: UseDocSuggestionPopoverArgs): UseDocSuggestionPopoverReturn {
  const [active, setActive] = useState<ActiveSuggestion | null>(null);
  // Guard re-entrant click events that bubble through the popover root.
  const lookupRef = useRef(suggestionsBySid);
  lookupRef.current = suggestionsBySid;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target) return;
      const sug = target.closest<HTMLElement>('.ed-suggestion[data-sid]');
      if (!sug || !container.contains(sug)) return;
      const sid = sug.dataset.sid;
      if (!sid) return;
      const data = lookupRef.current[sid];
      if (!data) return;
      e.preventDefault();
      e.stopPropagation();
      setActive({ anchorEl: sug, sid, data });
    };

    container.addEventListener('click', onClick);
    return () => {
      container.removeEventListener('click', onClick);
    };
  }, [containerRef]);

  const close = useCallback(() => setActive(null), []);

  const dismiss = useCallback(
    (sid?: string) => {
      const targetSid = sid ?? active?.sid;
      const container = containerRef.current;
      if (targetSid && container) {
        container
          .querySelectorAll<HTMLElement>(`.ed-suggestion[data-sid="${CSS.escape(targetSid)}"]`)
          .forEach((el) => el.classList.add('ed-dismissed'));
      }
      setActive(null);
    },
    [active, containerRef],
  );

  return { active, open: !!active, close, dismiss };
}
